import { debounce, isMobileBreakpoint, getLargestHeight, checkBrowserType } from "../utility/UtilityFunctions";
import { scrollDirection } from "../utility/ScrollDirection";
import { resetStates } from "../utility/ResetStates";
import { optimisedScrollAnimation } from "../utility/OptimisedScroll";

/**
 * Used to setup site critical js such as mobile/desktop switcher, optimised scroll event configurations etc...
 *
 * This should be run before all other theme related js
 */
export default class SiteVitals {
    constructor() {
        this.elements = {
            root: document.documentElement,
            html: document.documentElement,
            body: document.body,
            announcementBanner: document.querySelector('.c-announcement-header-banner'),
            primaryNav: document.querySelector('.c-nav-primary'),
            primaryNavSubElements: document.querySelectorAll('.menu .menu__item, .menu > .menu__item > a, .sub-menu'),
        };

        this.classes = {
            isActive: 'is-active',
            navActive: 'navigation-is-active',
        };

        // *=====*-- Register Custom Scroll Event --*=====* \\
        // use for other events to bind to
        optimisedScrollAnimation("scroll", "optimisedScroll");

        // *=====*-- Check current browser --*=====* \\
        // use to set browserspecific classes to the body
        checkBrowserType()

        // set the initial --css-variable on the root to ensure no load popping occurs due to resize delay
        if (this.elements.announcementBanner) {
            if (!this.elements.announcementBanner.querySelectorAll('.swiper-slide')) {
                const largestSlideHeight = getLargestHeight(this.elements.announcementBanner.querySelectorAll('.swiper-slide')) / 16;
                this.elements.root.style.setProperty('--announcement-banner-height', `${largestSlideHeight + 2}rem`);
            } else {
                this.elements.root.style.setProperty('--announcement-banner-height', `${(this.elements.announcementBanner.querySelector('p').getBoundingClientRect().height / 16) + 2}rem`);
            }
        }

        this.bindEventListeners();
    }

    bindEventListeners() {
        window.addEventListener('optimisedScroll', () => {
            // sets window object's values based on scrollDirection return values
            window.scrollDirection = scrollDirection();

            // adds targetable data attribute to html
            if (this.elements.html.classList.contains('navigation-is-active')) {
                return this.elements.html.setAttribute('data-scroll-direction', `up`);
            }

            this.elements.html.setAttribute('data-scroll-direction', `${window?.scrollDirection?.direction}`);

            // If the page is scrolled apply the body class
            this.pageIsScrolled()
        });

        // * Update the scroll-direction data attribute when scrolling has stopped to reshow header
        let timeoutId;
        window.addEventListener('optimisedScroll', debounce(() => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            timeoutId = setTimeout(() => {
                this.elements.html.setAttribute('data-scroll-direction', 'up');
            }, 700);
        }, 500));

        window.addEventListener('resize', debounce(() => {
            // check if mobile display is active on resize - ties into _base.scss breakpoint display attribute
            isMobileBreakpoint();

            // Add announcement banner height variable if set
            if (this.elements.announcementBanner) {
                if (!this.elements.announcementBanner.querySelectorAll('.swiper-slide')) {
                    const largestSlideHeight = getLargestHeight(this.elements.announcementBanner.querySelectorAll('.swiper-slide')) / 16;
                    this.elements.root.style.setProperty('--announcement-banner-height', `${largestSlideHeight + 2}rem`);
                } else {
                    this.elements.root.style.setProperty('--announcement-banner-height', `${(this.elements.announcementBanner.querySelector('p').getBoundingClientRect().height / 16) + 2}rem`);
                }
            } else {
                this.elements.root.style.setProperty('--announcement-banner-height', `0rem`);
            }
        }, 250));

        window.dispatchEvent(new Event('resize'));
        window.dispatchEvent(new Event('optimisedScroll')); // applies scrollDirection on load
    }

    // On page scroll add class to body
    pageIsScrolled() {
        let triggered = false;
        // IFFE callback to run the function when called
        return (() => {
            if (!triggered) {
                triggered = true;
                requestAnimationFrame(() => {
                    const threshold = window.innerWidth >= 768 ? 50 : 0;
                    document.body.classList.toggle("page-is-scrolled", window.pageYOffset > threshold);
                    triggered = false;
                });
            }
        })();
    }
}